import type { ESocialAuthProvider } from "./auth";
import type { EPushPlatform } from "./notifications";

// NOTE: NSAI-1606 — the decoded GET /app-config: every deployment-level
// setting the platform publishes, grouped by what it is about. Mirrors the
// server's IAppConfigResponse (server/src/app-config/app-config.types.ts)
// field for field; the wire format itself is obfuscated, see
// @frontend-shared/constants/app-config-wire-keys.
//
// Every group and every field is always present once decoded: a boolean the
// server leaves out, or sends as something that is not a boolean, decodes to
// its safe default (see @frontend-shared/schema/app-config), so a client never
// has to tell "off" apart from "missing".
export interface IAppConfigResponse {
  deployment: IAppConfigDeployment;
  auth: IAppConfigAuth;
  features: IAppConfigFeatures;
  bots: IAppConfigBots;
  meetings: IAppConfigMeetings;
  audio: IAppConfigAudio;
  map: IAppConfigMap;
  limits: IAppConfigLimits;
  analytics: IAppConfigAnalytics;
  legal: IAppConfigLegal;
  policies: IAppConfigPolicies;
  ai: IAppConfigAi;
}

// NOTE: What this server is, independent of any feature.
export interface IAppConfigDeployment {
  // NOTE: ENVIRONMENT is on-premise or closed-network — the same switch the
  // deploys use to pick the web's on-premise image.
  isOnPremise: boolean;
  // NOTE: NSAI-7701 — the deployment has no way out to the internet: the
  // mobile app stops reading the app-wide manifest on NodeShift's hosts, offers
  // no store link, and shows neither NodeShift's social links nor its meeting
  // bot's address. Optional: undefined is "unknown", which keeps the behaviour
  // from before the field.
  isClosedNetwork?: boolean;
  isBillingDisabled: boolean;
  // NOTE: NSAI-6314 — true on a closed-network deployment, where domain
  // ownership cannot be proven (the DNS TXT challenge needs an outbound lookup
  // the network cannot make). Both clients hide the SAML tab's "Verified
  // domains" section on it.
  isDomainVerificationUnavailable: boolean;
  flavor: IAppConfigFlavor;
  apiCapabilities: EServerApiCapability[];
  // NOTE: NSAI-7701 — the platforms this server can push to. The app asks for
  // notification permission and registers its device only on a listed one;
  // undefined is "unknown", and the app keeps asking.
  pushPlatforms?: EPushPlatform[];
  minMobileAppVersion?: string;
  // NOTE: NSAI-5890 — absent unless this server routes phone links through
  // the mobile link redirector; absent means "build links on your own origin".
  links?: IAppConfigLinks;
}

// NOTE: An unknown flavor key decodes as "nodeshift" — the default brand.
export type TDeploymentFlavorKey = "nodeshift" | "oplot";

export interface IAppConfigFlavor {
  key: TDeploymentFlavorKey;
  appName: string;
  companyName: string;
  defaultLanguage: string;
  logoUrl?: string;
}

// NOTE: NSAI-5890 — `shareBaseUrl` is `<redirector origin>/t/<tenant-host>
// <FRONTEND_URL path prefix>`, https, with no trailing slash, query or
// fragment. A client builds a chat share link on it in place of its own origin.
export interface IAppConfigLinks {
  shareBaseUrl: string;
}

// NOTE: NSAI-7414 — the twin of the server's EServerApiCapability, which names
// the endpoint behind each value. Add a value in the PR that adds its endpoint,
// on both sides. Values this client does not know are dropped at decode.
export enum EServerApiCapability {
  CONTENT_REPORTS = "contentReports",
  PAGE_PUBLIC_SHARE = "pagePublicShare",
  MEMORY_POLICY = "memoryPolicy",
  DEEP_RESEARCH_BUDGET = "deepResearchBudget",
  SEARCH_EMBEDDING_MODEL = "searchEmbeddingModel",
  PROBE_MODELS = "probeModels",
  SUMMARY_MODEL = "summaryModel",
  PROMPT_IMPROVEMENT_MODEL = "promptImprovementModel",
  CONNECTOR_APPROVAL = "connectorApproval",
  DATA_RETENTION = "dataRetention",
  CUSTOM_INSTRUCTIONS = "customInstructions",
  AI_GENERATED_FILES = "aiGeneratedFiles",
  PII_IMAGE_DESCRIPTION_MODEL = "piiImageDescriptionModel",
  EXTERNAL_API_LIMITS = "externalApiLimits",
  USER_MEMORY_IMPORT = "userMemoryImport",
  MY_TOKEN_USAGE = "myTokenUsage",
}

// NOTE: Which ways into this deployment actually work.
export interface IAppConfigAuth {
  isSsoVisible: boolean;
  // NOTE: NSAI-6323 — SSO is the deployment's only way in. Both clients drop
  // the password form, the forgot-password link and the sign-up link, leaving
  // the SSO button alone. Never true without isSsoVisible.
  isPasswordAuthDisabled: boolean;
  // NOTE: REGISTRATION_ENABLED and a free sign-up seat.
  isSignUpEnabled: boolean;
  // NOTE: Providers whose sign-in completes on this server; empty on-premise.
  socialProviders: ESocialAuthProvider[];
  isPasswordResetSelfService: boolean;
  isSamlSeatTiersEnabled: boolean;
}

// NOTE: Product feature switches. The `*FF` twins make a feature AVAILABLE for
// a device that opted in, without showing it to everyone — they are resolved
// through @frontend-shared/constants/feature-flags, never read directly.
export interface IAppConfigFeatures {
  isAgentsEnabled: boolean;
  isSkillsEnabled: boolean;
  // NOTE: NSAI-3309 — the Skills flag is on AND a sandbox runner publishes
  // readiness. False is the fail-closed answer.
  isSkillScriptExecutionAvailable: boolean;
  isCustomIntegrationEnabled: boolean;
  // NOTE: NSAI-3719 — the Code section is served by this deployment. False
  // hides the sidebar entry and the /code routes: with the flag off every
  // /code-sessions request is refused server-side.
  isCodeSessionsEnabled: boolean;
  isExternalIntegrationsProviderEnabled: boolean;
  isDocRecognitionEnabled: boolean;
  isBrandedPresentationsEnabled: boolean;
  isUserMemoryEnabled: boolean;
  isZipArchiveUploadEnabled: boolean;
  isAdminPanelEnabled: boolean;
  // NOTE: NSAI-5880 — gates the Organization → Models "Pending approval"
  // section; the server withholds pending models on its own either way.
  isModelApprovalEnabled: boolean;
  isSyncatorApiEnabled: boolean;
  // NOTE: NSAI-1249 — the shadow-drop switch for the mobile chat-search entry
  // point. Web has its own always-on chat search and ignores it.
  isMobileChatSearchEnabled: boolean;
  isTokenUsageEnabled: boolean;
  isTokenCostUsageEnabled: boolean;
  isUserAnalyticsEnabled: boolean;
  isTokenLimitsEnabled: boolean;
  isCallAssistantFeaturesEnabled: boolean;
  isCallAssistantFeaturesEnabledFF: boolean;
  isAnonymizationLabEnabled: boolean;
  isAnonymizationLabEnabledFF: boolean;
  // NOTE: NSAI-8174 — model subscription connections: the everyone-flag and its
  // soft-launch twin. Both false on on-premise installs.
  isModelSubscriptionConnectionsEnabled: boolean;
  isModelSubscriptionConnectionsEnabledFF: boolean;
  isFinancialMaskingFeatureEnabled: boolean;
  isPiiCustomEntitiesFeatureEnabled: boolean;
  isPiiAdvancedRulesFeatureEnabled: boolean;
  isPiiIntentRoutingFeatureEnabled: boolean;
  // NOTE: NSAI-3060 — gates the Settings → SIEM export tab.
  isSiemExportEnabled: boolean;
  // NOTE: NSAI-5477 — the deployment-wide SIEM transport points at a real
  // collector, so saving an organization collector REPLACES it for that
  // organization's events. The SIEM Export screen warns about it.
  isSiemGlobalCollectorActive: boolean;
}

// NOTE: Chat bots the deployment runs.
export interface IAppConfigBots {
  isTelegramBotEnabled: boolean;
  // NOTE: NSAI-6224 — mutually exclusive with isWhatsappBotEnabledFF: the
  // server sets the soft-launch twin instead of this one while the Settings
  // section is withheld from everyone but opted-in devices.
  isWhatsappBotEnabled: boolean;
  isWhatsappBotEnabledFF: boolean;
  isMatrixBotEnabled: boolean;
  // NOTE: Install URLs, each absent when its bot is not configured.
  slackBotUri?: string;
  teamsBotUri?: string;
}

// NOTE: Meeting platforms and calendars the call assistant can join.
export interface IAppConfigMeetings {
  isKonturTalkEnabled: boolean;
  isYandexTelemostEnabled: boolean;
  isSaluteJazzEnabled: boolean;
  jitsiCustomDomains: string[];
  enabledCalendars: string[];
  // NOTE: NSAI-7701 — the address users invite the meeting bot at. Absent, a
  // client keeps the address it shipped with, or names none on a closed
  // network.
  meetingBotEmail?: string;
}

export interface IAppConfigAudio {
  isAudioTranscriptionEnabled: boolean;
  isExternalServiceForAudioTranscription: boolean;
  isTtsEnabled: boolean;
  isExternalServiceForTts: boolean;
  ttsVoices: string[];
}

// NOTE: NSAI-5934 — the chat-map tile server. Absent `tileUrl` means the
// basemap is off; the other two only ever arrive alongside it.
export interface IAppConfigMap {
  tileUrl?: string;
  tileAttribution?: string;
  maxZoom?: number;
}

export interface IAppConfigLimits {
  maxFileUploadCount: number;
  // NOTE: NSAI-7817 — the knowledge upload's per-file cap in MB, checked
  // before uploading; absent, the client skips the pre-check.
  knowledgeMaxFileSizeMb?: number;
}

export interface IAppConfigAnalytics {
  isEnabled: boolean;
  // NOTE: Only a PostHog PROJECT key (phc_) survives decoding.
  posthogKey?: string;
  posthogHost?: string;
}

// NOTE: Every URL here is https or absent: the decoder drops anything else.
export interface IAppConfigLegal {
  supportEmail?: string;
  privacyPolicyUrl?: string;
  termsUrl?: string;
}

export interface IAppConfigPolicies {
  isAccountDeletionEnabled: boolean;
}

export interface IAppConfigAi {
  providers: IAppConfigAiProvider[];
}

export interface IAppConfigAiProvider {
  key: string;
  name: string;
  isSelfHosted: boolean;
}

// NOTE: The groups that hold the boolean switches a feature flag can point at.
export type TAppConfigFlagGroup = "features" | "bots";

type TBooleanKeys<TGroup> = {
  [TKey in keyof TGroup]-?: TGroup[TKey] extends boolean ? TKey : never;
}[keyof TGroup];

// NOTE: A typed reference to one boolean switch, e.g.
// `{ group: "bots", key: "isWhatsappBotEnabled" }`.
export type TAppConfigFlagRef = {
  [TGroup in TAppConfigFlagGroup]: {
    group: TGroup;
    key: TBooleanKeys<IAppConfigResponse[TGroup]>;
  };
}[TAppConfigFlagGroup];

// NOTE: The obfuscated payload as it travels. Typed loosely on purpose: it
// comes from whichever server the client points at and is validated by
// fromWireFormat before anything reads it.
export type TWireAppConfig = Record<string, unknown>;
